import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import z from 'zod';

import type { CreateCarrierDTO } from '@invenco/shipping-interface';

import { DialogBody, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { TextFormField } from '@/components/forms/text-form-field';
import type { Result } from '@/shared/helpers/Result';

const BASE_TRACKING_URL_MESSAGE =
  'Must include <tracking_number> in the URL, which is replaced with the tracking number';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter carrier name'),
  code: z.string().trim().min(1, 'Enter carrier code'),
  baseTrackingUrl: z
    .string()
    .trim()
    .refine((value) => value === '' || value.includes('<tracking_number>'), {
      message: BASE_TRACKING_URL_MESSAGE,
    }),
});

type Props = {
  onSubmit: (data: CreateCarrierDTO) => Promise<Result> | Promise<void> | void;
};

export function CreateCarrierDialogContent({ onSubmit }: Props) {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      code: '',
      baseTrackingUrl: '',
    },
  });

  return (
    <FormDialogContent
      form={form}
      submitLabel="Create"
      aria-describedby={undefined}
      onSubmit={(data) =>
        onSubmit({
          name: data.name,
          code: data.code,
          baseTrackingUrl: data.baseTrackingUrl === '' ? undefined : data.baseTrackingUrl,
        })
      }
    >
      <DialogHeader>
        <DialogTitle>Add carrier</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <TextFormField control={form.control} name="name" label="Carrier name" />
        <TextFormField control={form.control} name="code" label="Carrier code" />
        <TextFormField
          control={form.control}
          name="baseTrackingUrl"
          label="Tracking URL template"
          description="Must include <tracking_number> in the URL."
        />
      </DialogBody>
    </FormDialogContent>
  );
}
